import { z } from 'zod';
import type { Permission } from './access.ts';

export const TAG_OBJECT_TYPES = ['campaign', 'canvas', 'segment', 'template', 'content_block', 'event_definition', 'attribute_definition', 'media_asset'] as const;
export const TagObjectType = z.enum(TAG_OBJECT_TYPES);
export type TagObjectType = z.infer<typeof TagObjectType>;

/** Who may change the tags of an object: the people who may edit it. */
export const TAG_EDIT_PERMISSIONS: Record<TagObjectType, Permission> = {
  campaign: 'campaigns.edit',
  canvas: 'canvas.edit',
  segment: 'segments.edit',
  template: 'content.edit',
  content_block: 'content.edit',
  media_asset: 'content.edit',
  event_definition: 'catalog.edit',
  attribute_definition: 'catalog.edit',
};

/** Creating, renaming or deleting a tag: anyone who edits something taggable. */
export const TAG_MANAGE_PERMISSIONS: readonly Permission[] = [...new Set(Object.values(TAG_EDIT_PERMISSIONS))];

const Color = z.string().regex(/^#[0-9a-f]{6}$/i, 'color must be #rrggbb');

export const CreateTagBody = z.object({ name: z.string().max(200), color: Color.nullable().optional() });
export type CreateTagBody = z.infer<typeof CreateTagBody>;
export const UpdateTagBody = z.object({ name: z.string().max(200).optional(), color: Color.nullable().optional() }).refine((b) => b.name !== undefined || b.color !== undefined, 'nothing to change');
export type UpdateTagBody = z.infer<typeof UpdateTagBody>;
export const DeleteTagQuery = z.object({ force: z.enum(['true', 'false']).optional() });

/** A tag with how many objects of each type use it (Manage Tags). */
export const TagView = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string().nullable(),
  usage: z.partialRecord(TagObjectType, z.number().int()),
  total: z.number().int(),
});
export type TagView = z.infer<typeof TagView>;

export const SetObjectTagsBody = z.object({ tagIds: z.array(z.uuid()).max(50) });
export type SetObjectTagsBody = z.infer<typeof SetObjectTagsBody>;

export const SEARCH_OBJECT_TYPES = ['app', 'internal_group', 'attribute_definition', 'canvas', 'campaign', 'segment', 'template', 'content_block', 'event_definition', 'media_asset'] as const;
export const SearchObjectType = z.enum(SEARCH_OBJECT_TYPES);

export const SearchQuery = z.object({
  q: z.string().trim().min(1).max(200),
  /** Comma-separated object types. */
  types: z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').map((t) => t.trim()).filter(Boolean) : undefined))
    .pipe(z.array(SearchObjectType).optional()),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type SearchQuery = z.infer<typeof SearchQuery>;

export const SearchHit = z.object({
  objectType: SearchObjectType,
  objectId: z.uuid(),
  apiId: z.string().nullable(),
  title: z.string(),
  subtitle: z.string().nullable(),
  url: z.string(),
  tags: z.array(z.string()),
});
export type SearchHit = z.infer<typeof SearchHit>;
